import React from 'react';
import { useTranslation } from 'react-i18next';
import { Gavel, Map as MapIcon } from '@mui/icons-material';
import { Team } from '../../models/Team';
import TeamLogo from '../../components/TeamLogo';
import styles from './MatchPage2.module.css';
import inferno from '../../content/maps/de_inferno.jpg';
import mirage from '../../content/maps/de_mirage.jpg';
import nuke from '../../content/maps/de_nuke.jpg';
import overpass from '../../content/maps/de_overpass.jpg';
import ancient from '../../content/maps/de_ancient.jpg';
import vertigo from '../../content/maps/de_vertigo.jpg';
import anubis from '../../content/maps/de_anubis.jpg';
import dust2 from '../../content/maps/de_dust2.jpg';
import train from '../../content/maps/de_train.jpg';
import cache from '../../content/maps/de_cache.jpg';

interface Match2VetoProps {
    vetoLog: string;
    team1: Team;
    team2: Team;
}

const mapImages: { [key: string]: string } = {
    Inferno: inferno,
    Mirage: mirage,
    Nuke: nuke,
    Overpass: overpass,
    Vertigo: vertigo,
    Ancient: ancient,
    Anubis: anubis,
    Dust2: dust2,
    Train: train,
    Cache: cache,
};

type VetoAction = 'ban' | 'pick' | 'decider';

interface VetoCard {
    action: VetoAction;
    teamName: string | null;
    map: string;
    side?: { team: string; side: 'CT' | 'T' };
}

// Parses the vetoLog into cards. Same line grammar as the old Veto component
// ("removed" / "picked" / "was left over" / "choose to start"), but side choices
// are attached to the preceding pick/decider card instead of getting own cards.
const parseVeto = (vetoLog: string): VetoCard[] => {
    const cards: VetoCard[] = [];
    const lines = vetoLog
        .split('\n')
        .map(l => l.trim().replace(/^\d{1,2}:\d{2}(:\d{2})?\s*-\s*/, '')) // tolerate timestamped logs
        .filter(l => l.length > 0);

    for (const line of lines) {
        if (line.includes(' removed ')) {
            const [teamName, rest] = line.split(' removed ');
            cards.push({ action: 'ban', teamName, map: rest.split(' ')[0] });
        } else if (line.includes(' picked ')) {
            const [teamName, rest] = line.split(' picked ');
            cards.push({ action: 'pick', teamName, map: rest.split(' ')[0] });
        } else if (line.includes(' was left over')) {
            cards.push({ action: 'decider', teamName: null, map: line.split(' was left over')[0].trim().split(' ')[0] });
        } else if (line.includes(' choose to start ')) {
            const [teamName, rest] = line.split(' choose to start ');
            const side = rest.trim().split(' ')[0].toUpperCase() === 'CT' ? 'CT' : 'T';
            const target = cards[cards.length - 1];
            if (target) target.side = { team: teamName, side };
        }
    }
    return cards;
};

const ACTION_STYLES: Record<VetoAction, { card: string; map: string; action: string }> = {
    ban: { card: '', map: styles.vetoMapBan, action: styles.vetoActionBan },
    pick: { card: styles.vetoCardPick, map: styles.vetoMapPick, action: styles.vetoActionPick },
    decider: { card: styles.vetoCardDecider, map: styles.vetoMapDecider, action: styles.vetoActionDecider },
};

// Veto strip: BANNED (red) / PICKED (cyan) / DECIDER (blue) cards with tinted
// map backgrounds, order number badge and starting-side info.
const Match2Veto: React.FC<Match2VetoProps> = ({ vetoLog, team1, team2 }) => {
    const { t } = useTranslation('pages');
    const cards = parseVeto(vetoLog);
    if (cards.length === 0) return null;

    const matchTeam = (name: string | null): Team | null => {
        if (!name) return null;
        const clean = name.trim().toLowerCase();
        if (team1.teamName?.trim().toLowerCase() === clean) return team1;
        if (team2.teamName?.trim().toLowerCase() === clean) return team2;
        return null;
    };

    const actionLabel: Record<VetoAction, string> = {
        ban: t('match2.vetoBanned'),
        pick: t('match2.vetoPicked'),
        decider: t('match2.vetoDecider'),
    };

    return (
        <div className={styles.vetoSection}>
            <div className={styles.vetoLabel}>
                <Gavel sx={{ fontSize: 16 }} />
                {t('match2.veto')}
            </div>
            <div className={styles.vetoRow}>
                {cards.map((card, i) => {
                    const team = matchTeam(card.teamName);
                    const img = mapImages[card.map];
                    const cls = ACTION_STYLES[card.action];
                    return (
                        <div key={i} className={`${styles.vetoCard} ${cls.card}`}>
                            <div className={styles.vetoNum}>{i + 1}</div>
                            <div
                                className={`${styles.vetoMap} ${cls.map}`}
                                style={img ? {
                                    background: `${getComputedGradient(card.action)}, url(${img}) center / cover`,
                                } : undefined}
                            >
                                {!img && <MapIcon sx={{ fontSize: 38 }} />}
                            </div>
                            <div className={styles.vetoBody}>
                                <div className={`${styles.vetoAction} ${cls.action}`}>{actionLabel[card.action]}</div>
                                <div className={styles.vetoTeamRow}>
                                    {team && (
                                        <TeamLogo logo={team.teamLogo} name={team.teamName} className={styles.vetoTeamLogo} />
                                    )}
                                    <span className={styles.vetoTeamName}>
                                        {card.teamName ?? t('match2.vetoLeftover')}
                                    </span>
                                </div>
                                <div className={`${styles.vetoMapName} ${cls.action}`}>{card.map}</div>
                                {card.side && (
                                    <div className={styles.vetoSide}>
                                        {t(card.side.side === 'CT' ? 'match2.startsCt' : 'match2.startsT', { team: card.side.team })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Tinted overlay over the real map screenshot, per action (mock uses these hues
// as full gradients; here they sit on top of the map image).
const getComputedGradient = (action: VetoAction): string => {
    switch (action) {
        case 'ban': return 'linear-gradient(160deg, rgba(58,38,38,0.78), rgba(26,20,20,0.88))';
        case 'pick': return 'linear-gradient(160deg, rgba(31,58,64,0.72), rgba(20,32,31,0.85))';
        case 'decider': return 'linear-gradient(160deg, rgba(36,52,74,0.72), rgba(20,27,38,0.85))';
    }
};

export default Match2Veto;
